/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withFormik } from 'formik';
import * as Yup from 'yup';

import { fetchPaises } from 'actions/registro';
import { getPaisesOptions, getDocumentosOptions } from 'utils/utils';
import { paisesSelector } from 'store/selectors';

import {
  RenderField,
  SelectField,
  TextField,
  CaptchaField,
} from 'components/RenderField (Nuevo)';
import ButtonSolid from 'components/ButtonSolid';
import ButtonLink from 'components/ButtonLink';
import Card from 'components/Card';
import TitleCard from 'components/TitleCard';
import Box from 'components/Box';
import {
  DOCUMENTO_MENSAJE,
  REQUERIDO,
  MAX_LEN_NAMES,
  getMensajeErrorLargoMaximo,
  CORREO_INVALIDO,
  FORMATO_CI_INVALIDO,
  TEXTO_NUMERO_TELEFONO,
  TEXTO_PLACEHOLDER_NUMERO_TELEFONO,
  TEXTO_NUMERO_TELEFONO_AVISO,
  PAIS_EMISOR,
  TIPO_DOCUMENTO,
  UY_CODE,
} from 'constants/commonConstants';
import { validEmail, formatoCI, documentValidator } from 'utils/validators';

import acDigital from 'assets/ac-digital.svg';
import acPresencial from 'assets/ac-presencial.svg';

// react-phone-input
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import es from 'react-phone-input-2/lang/es.json';

import './contacto.css';

const ContactAside = () => (
  <div className="contacto__aside">
    <h2 className="contacto__aside__title">Atención a la ciudadanía</h2>
    <p className="caption contacto__aside__subtitle">
      Por consultas sobre información de Trámites y Servicios del Estado podés
      dirigirte a nuestros canales de Atención a la Ciudadanía.
    </p>
    <div className="contacto__aside__section">
      <p className="caption contacto__aside__section__title">DIGITAL</p>
      <img
        src={acDigital}
        alt="Contacto a través de medios digitales"
        width="48"
        height="48"
      />
      <div className="contacto__aside__section__info">
        <ButtonLink
          title="info@atencionalaciudadania.gub.uy"
          to="mailto:info@atencionalaciudadania.gub.uy"
          external
        />
      </div>
    </div>
    <div className="contacto__aside__section">
      <p className="caption contacto__aside__section__title">PRESENCIAL</p>
      <img
        src={acPresencial}
        alt="Contacto de forma presencial"
        width="48"
        height="48"
      />
      <div className="contacto__aside__section__info">
        <ButtonLink
          title="Puntos de Atención a la Ciudadanía"
          to="http://atencionciudadana.gub.uy/"
          external
        />
      </div>
    </div>
  </div>
);

const Form = props => {
  const {
    values,
    touched,
    handleChange,
    handleSubmit,
    handleBlur,
    setFieldValue,
    asuntosOptions,
    isSubmitting,
    errors,
    envioContactoError,
    recaptchaRef,
  } = props;

  const dispatch = useDispatch();
  const paises = useSelector(paisesSelector);
  const [paisesOptions, setPaisesOptions] = useState([]);
  const [documentosOptions, setDocumentosOptions] = useState([]);

  useEffect(() => {
    if (!paises.length) {
      dispatch(fetchPaises());
    }
  }, []);

  useEffect(() => {
    if (paises.length) {
      // eslint-disable-next-line no-shadow
      const { paisesOptions } = getPaisesOptions(paises);
      setPaisesOptions(paisesOptions);
      if (!values.logueado)
        setFieldValue('pais_emisor', paisesOptions[0]?.value);
    }
  }, [paises]);

  useEffect(() => {
    // eslint-disable-next-line no-shadow, no-unused-vars
    const { documentosOptions, tipoDocumento } = getDocumentosOptions(
      values.pais_emisor,
      paises,
    );
    if (documentosOptions) setDocumentosOptions(documentosOptions);
    if (!values.logueado) setFieldValue('tipo_documento', tipoDocumento);
  }, [values.pais_emisor]);

  const handleOnFocusNumeroTelefono = valor => {
    if (!values.telefono && valor.key !== 'Backspace') {
      setFieldValue('telefono', '+598');
    }
  };

  const handleOnBlurNumeroTelefono = () => {
    if (values.telefono.length <= 4) {
      setFieldValue('telefono', '');
    }
  };

  return (
    <main
      className="contacto__container__form"
      aria-labelledby="formulario-contacto"
    >
      <form onSubmit={handleSubmit} id="formulario-contacto">
        <Box title="Datos de Contacto">
          <RenderField
            name="nombre_completo"
            type="text"
            value={values.nombre_completo}
            expanded
            label="Nombre completo"
            onChange={handleChange}
            required
            hidePlaceholder
            disabled={isSubmitting}
            error={
              envioContactoError !== null &&
              envioContactoError.field === 'nombre_completo'
                ? envioContactoError.error
                : errors.nombre_completo
            }
            touched={touched.nombre_completo}
          />
          <div>
            <SelectField
              name="pais_emisor"
              label={PAIS_EMISOR}
              required
              expanded
              error={errors.paisEmisor}
              onChange={handleChange}
              onBlur={handleChange}
              data={paisesOptions}
              value={values.pais_emisor}
              touched={touched.pais_emisor}
            />
            <div>
              {documentosOptions && documentosOptions.length !== null && (
                <SelectField
                  name="tipo_documento"
                  label={TIPO_DOCUMENTO}
                  small
                  expanded
                  required
                  disabled={documentosOptions.length === 1}
                  data={documentosOptions}
                  error={errors.tipoDocumento}
                  onChange={handleChange}
                  onBlur={handleChange}
                  value={values.tipo_documento}
                  touched={touched.tipo_documento}
                />
              )}
              <RenderField
                name="numero_documento"
                type="text"
                value={values.numero_documento}
                expanded
                hasTooltip
                msg={DOCUMENTO_MENSAJE}
                label="Número de documento"
                onChange={handleChange}
                onBlur={handleBlur}
                required
                hidePlaceholder
                disabled={isSubmitting}
                error={
                  envioContactoError !== null &&
                  envioContactoError.field === 'numero_documento'
                    ? envioContactoError.error
                    : errors.numero_documento
                }
                touched={touched.numero_documento}
                className="contacto__field-popup"
              />
            </div>
          </div>
          <RenderField
            name="email"
            type="email"
            value={values.email}
            expanded
            label="Correo electrónico"
            onChange={handleChange}
            onBlur={handleBlur}
            required
            hidePlaceholder
            disabled={isSubmitting}
            error={
              envioContactoError !== null &&
              envioContactoError.field === 'email'
                ? envioContactoError.error
                : errors.email
            }
            touched={touched.email}
          />
          <div>
            <label>
              {TEXTO_NUMERO_TELEFONO}
              <div className="contenedorNumeroTelefonoDisabled">
                <PhoneInput
                  placeholder={TEXTO_PLACEHOLDER_NUMERO_TELEFONO}
                  country="uy"
                  value={values.telefono}
                  onChange={valor => {
                    setFieldValue('telefono', valor);
                  }}
                  localization={es}
                  enableLongNumbers
                  preferredCountries={['uy']}
                  disableCountryCode={!values.telefono}
                  onFocus={handleOnFocusNumeroTelefono}
                  onBlur={handleOnBlurNumeroTelefono}
                  isValid={() => {
                    if (touched.telefono && errors.telefono) {
                      return errors.telefono;
                    }
                    return true;
                  }}
                />
                <p className="help_text">{TEXTO_NUMERO_TELEFONO_AVISO}</p>
              </div>
            </label>
          </div>
          <SelectField
            name="asunto"
            expanded
            label="Asunto"
            onBlur={handleChange}
            data={asuntosOptions}
            value={values.asunto}
            onChange={handleChange}
            defaultOption="Seleccione un asunto"
            required
            hidePlaceholder
            disabled={isSubmitting}
            error={
              envioContactoError !== null &&
              envioContactoError.field === 'asunto'
                ? envioContactoError.error
                : errors.asunto
            }
            touched={touched.asunto}
          />
          <TextField
            name="mensaje"
            expanded
            label="Motivo solicitud"
            onChange={handleChange}
            required
            hidePlaceholder={false}
            placeholder="Escribe acá el motivo de la videollamada"
            disabled={isSubmitting}
            error={
              envioContactoError !== null &&
              envioContactoError.field === 'mensaje'
                ? envioContactoError.error
                : errors.mensaje
            }
            touched={touched.mensaje}
          />
          <div className="captchaContainer">
            <CaptchaField
              name="captcha_response"
              setFieldValue={setFieldValue}
              recaptchaRef={recaptchaRef}
              error={
                envioContactoError !== null &&
                envioContactoError.field === 'captcha_response'
                  ? envioContactoError.error
                  : errors.captcha_response
              }
              touched={touched.captcha_response}
            />
          </div>
          <div className="botonContainer">
            <ButtonSolid
              text="Continuar"
              ariaLabel="Enviar formulario de contacto"
              isSubmit
              isLoading={isSubmitting}
            />
          </div>
        </Box>
      </form>
    </main>
  );
};

const ContactForm = withFormik({
  mapPropsToValues: props => ({
    nombre_completo: props.nombreCompleto,
    numero_documento: props.numeroDocumento,
    email: props.email,
    telefono: props.telefono,
    asunto: '',
    mensaje: '',
    captcha_response: '',
    pais_emisor: props.paisEmisor,
    tipo_documento: props.tipoDocumento,
    logueado: props.logueado,
  }),

  validationSchema: Yup.object().shape({
    nombre_completo: Yup.string()
      .required(REQUERIDO)
      .max(MAX_LEN_NAMES, getMensajeErrorLargoMaximo(MAX_LEN_NAMES)),
    numero_documento: Yup.string()
      .required(REQUERIDO)
      .test('validDocument', FORMATO_CI_INVALIDO, (value, context) => {
        // eslint-disable-next-line camelcase
        const { pais_emisor } = context.parent;
        if (value)
          // eslint-disable-next-line camelcase
          return pais_emisor === UY_CODE
            ? formatoCI(value)
            : documentValidator(value);
        return true;
      }),
    email: Yup.string()
      .required(REQUERIDO)
      .test('validEmail', CORREO_INVALIDO, value => validEmail(value)),
    telefono: Yup.string().required(REQUERIDO),
    asunto: Yup.string().required(REQUERIDO),
    mensaje: Yup.string().required(REQUERIDO),
    // captcha_response: Yup.string().required(REQUERIDO),
    pais_emisor: Yup.string().required(REQUERIDO),
    tipo_documento: Yup.string().required(REQUERIDO),
  }),

  handleSubmit: async (values, bag) => {
    await bag.props.enviarMensaje(values, bag.setFieldValue);
    bag.setSubmitting(false);
  },
})(Form);

const Contacto = props => (
  <div className="contacto">
    <Card className="contacto--card">
      <TitleCard title={props.title} />
      <div className="headline">{props.subtitle}</div>
      <section className="contacto__container">
        <ContactForm
          nombreCompleto={
            props.currentUser !== null
              ? `${props.currentUser.nombre_completo}`
              : ''
          }
          paisEmisor={
            props.currentUser !== null
              ? props.currentUser.documento.pais_emisor.codigo
              : null
          }
          tipoDocumento={
            props.currentUser !== null
              ? props.currentUser.documento.tipo_documento.codigo
              : null
          }
          numeroDocumento={
            props.currentUser !== null ? props.currentUser.numero_documento : ''
          }
          email={
            props.currentUser !== null ? props.currentUser.user?.email : ''
          }
          telefono={
            props.currentUser !== null ? props.currentUser.numero_telefono : ''
          }
          envioContactoError={props.envioContactoError}
          enviarMensaje={props.enviarMensaje}
          recaptchaRef={props.recaptchaRef}
          asuntosOptions={props.asuntosOptions}
          logueado={props.currentUser !== null}
        />
      </section>
    </Card>
    <ContactAside />
  </div>
);

export default Contacto;
